import React from 'react';
import { ROOM_COLORS } from '../../../../game/phaserFacility/facilityLayout';
import { gridToWorld } from '../facilityCoords';
import {
  cornerPillarOffsets,
  windowOffsets,
  FOUNDATION_HEIGHT,
  ROOF_THICKNESS,
} from '../buildingGeometry';
import { windowEmissiveScale } from '../dayNightCycle';
import {
  wallRibOffsets,
  windowPaneLayout,
  roofClutterItems,
} from '../buildingDetails';

const CONCRETE = '#23282b';
const ROOF_COLOR = '#2f3438';
const WINDOW_COLOR = '#9fd8ff';
const REVEAL_COLOR = '#0a0c0e';

/**
 * Standalone opaque industrial building for an unlocked room.
 * @param {object} roomDef - FACILITY_ROOMS entry
 * @param {boolean} isSelected
 * @param {boolean} isHovered
 * @param {function} onClick
 * @param {function} onPointerEnter
 * @param {function} onPointerLeave
 */
export function IndustrialBuilding({ roomDef, tick = 0, isSelected, isHovered, onClick, onPointerEnter, onPointerLeave }) {
  const { x, z, boxW, boxH, boxD } = gridToWorld(roomDef);
  const accent = (ROOM_COLORS[roomDef.type] || ROOM_COLORS.future).stroke;
  const wallH = boxH;
  const wallTop = FOUNDATION_HEIGHT + wallH;
  const winH = wallH * 0.4;
  const winCenterY = FOUNDATION_HEIGHT + wallH * 0.55;

  const winGlow = windowEmissiveScale(tick);
  const emissiveIntensity = isSelected ? 0.5 : isHovered ? 0.28 : 0.08;
  const pillars = cornerPillarOffsets(boxW, boxD);
  const windows = windowOffsets(boxW, 3);
  const panes = windowPaneLayout(windows, 0.5, winH);
  const ribs = wallRibOffsets(boxW, 5);
  const clutter = roofClutterItems(roomDef.id, boxW, boxD);
  const ringRadius = Math.max(boxW, boxD) * 0.62;
  const roofY = wallTop + ROOF_THICKNESS / 2 + 0.04;
  const parapetH = 0.12;
  const parapetT = 0.06;
  const interactive = !roomDef.noClick && !roomDef.ghost;

  const handlers = interactive
    ? {
        onClick: (e) => { e.stopPropagation(); onClick?.(); },
        onPointerEnter: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; onPointerEnter?.(); },
        onPointerLeave: () => { document.body.style.cursor = 'default'; onPointerLeave?.(); },
      }
    : {};

  return (
    <group position={[x, 0, z]} {...handlers}>
      {/* Foundation pad */}
      <mesh position={[0, FOUNDATION_HEIGHT / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[boxW + 0.12, FOUNDATION_HEIGHT, boxD + 0.12]} />
        <meshStandardMaterial color="#16191b" roughness={1} metalness={0} />
      </mesh>

      {/* Solid wall block */}
      <mesh position={[0, FOUNDATION_HEIGHT + wallH / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[boxW, wallH, boxD]} />
        <meshStandardMaterial
          color={CONCRETE}
          emissive={accent}
          emissiveIntensity={emissiveIntensity}
          roughness={0.9}
          metalness={0.05}
        />
      </mesh>

      {/* Corner pillars */}
      {pillars.map(([dx, dz], i) => (
        <mesh key={i} position={[dx, FOUNDATION_HEIGHT + wallH / 2, dz]} castShadow>
          <boxGeometry args={[0.18, wallH + 0.05, 0.18]} />
          <meshStandardMaterial color="#1a1d1f" roughness={0.9} metalness={0.15} />
        </mesh>
      ))}

      {/* Wall ribs (+Z and −Z faces) */}
      {ribs.map((rx, i) => (
        <React.Fragment key={`rib-${i}`}>
          <mesh position={[rx, FOUNDATION_HEIGHT + wallH / 2, boxD / 2 + 0.02]} castShadow>
            <boxGeometry args={[0.04, wallH, 0.03]} />
            <meshStandardMaterial color="#1c2023" roughness={0.95} metalness={0.05} />
          </mesh>
          <mesh position={[rx, FOUNDATION_HEIGHT + wallH / 2, -boxD / 2 - 0.02]} castShadow>
            <boxGeometry args={[0.04, wallH, 0.03]} />
            <meshStandardMaterial color="#1c2023" roughness={0.95} metalness={0.05} />
          </mesh>
        </React.Fragment>
      ))}

      {/* Accent stripe mid-height on +Z face */}
      <mesh position={[0, FOUNDATION_HEIGHT + wallH * 0.5, boxD / 2 + 0.012]}>
        <boxGeometry args={[boxW * 0.85, 0.08, 0.02]} />
        <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.15} roughness={0.5} metalness={0.2} />
      </mesh>

      {/* Window reveal frames (+Z) */}
      {windows.map((dx, i) => (
        <mesh key={`reveal-${i}`} position={[dx, winCenterY, boxD / 2 + 0.008]}>
          <boxGeometry args={[0.54, winH + 0.04, 0.03]} />
          <meshStandardMaterial color={REVEAL_COLOR} roughness={1} metalness={0} />
        </mesh>
      ))}

      {/* Window panes (+Z) */}
      {panes.map((pane, i) => (
        <mesh key={`pane-${i}`} position={[pane.dx, winCenterY + pane.dy, boxD / 2 + 0.015]}>
          <boxGeometry args={[pane.w, pane.h, 0.04]} />
          <meshStandardMaterial
            color={WINDOW_COLOR}
            emissive={WINDOW_COLOR}
            emissiveIntensity={0.12 * winGlow}
            transparent
            opacity={0.4}
            roughness={0.1}
            metalness={0.2}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Entry steps (front, +Z) */}
      <mesh position={[0, FOUNDATION_HEIGHT + 0.06, boxD / 2 + 0.18]} castShadow>
        <boxGeometry args={[0.8, 0.12, 0.36]} />
        <meshStandardMaterial color="#202426" roughness={1} metalness={0} />
      </mesh>

      {/* Door panel (front, +Z) */}
      <mesh position={[0, FOUNDATION_HEIGHT + wallH * 0.35, boxD / 2 + 0.015]}>
        <boxGeometry args={[0.55, wallH * 0.6, 0.05]} />
        <meshStandardMaterial color="#14171a" emissive={accent} emissiveIntensity={0.08 * winGlow} roughness={0.6} metalness={0.2} />
      </mesh>

      {/* Entry canopy above door */}
      <mesh position={[0, FOUNDATION_HEIGHT + wallH * 0.68, boxD / 2 + 0.1]} castShadow>
        <boxGeometry args={[1.0, 0.06, 0.35]} />
        <meshStandardMaterial color="#1a1d1f" emissive={accent} emissiveIntensity={0.25} roughness={0.7} metalness={0.15} />
      </mesh>

      {/* Roofline accent trim */}
      <mesh position={[0, wallTop + 0.005, 0]}>
        <boxGeometry args={[boxW + 0.1, 0.06, boxD + 0.1]} />
        <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={isSelected ? 0.8 : 0.4} roughness={0.4} metalness={0.3} />
      </mesh>

      {/* Roof slab */}
      <mesh position={[0, roofY, 0]} castShadow receiveShadow>
        <boxGeometry args={[boxW + 0.06, ROOF_THICKNESS, boxD + 0.06]} />
        <meshStandardMaterial color={ROOF_COLOR} roughness={0.7} metalness={0.35} />
      </mesh>

      {/* Roof parapet */}
      <mesh position={[0, roofY + ROOF_THICKNESS / 2 + parapetH / 2, boxD / 2 + 0.03]}>
        <boxGeometry args={[boxW + 0.06, parapetH, parapetT]} />
        <meshStandardMaterial color={ROOF_COLOR} roughness={0.8} metalness={0.3} />
      </mesh>
      <mesh position={[0, roofY + ROOF_THICKNESS / 2 + parapetH / 2, -boxD / 2 - 0.03]}>
        <boxGeometry args={[boxW + 0.06, parapetH, parapetT]} />
        <meshStandardMaterial color={ROOF_COLOR} roughness={0.8} metalness={0.3} />
      </mesh>
      <mesh position={[-boxW / 2 - 0.03, roofY + ROOF_THICKNESS / 2 + parapetH / 2, 0]}>
        <boxGeometry args={[parapetT, parapetH, boxD + 0.06]} />
        <meshStandardMaterial color={ROOF_COLOR} roughness={0.8} metalness={0.3} />
      </mesh>
      <mesh position={[boxW / 2 + 0.03, roofY + ROOF_THICKNESS / 2 + parapetH / 2, 0]}>
        <boxGeometry args={[parapetT, parapetH, boxD + 0.06]} />
        <meshStandardMaterial color={ROOF_COLOR} roughness={0.8} metalness={0.3} />
      </mesh>

      {/* Seeded roof clutter */}
      {clutter.map((item, i) => {
        const s = item.scale;
        const baseY = roofY + ROOF_THICKNESS / 2;
        switch (item.kind) {
          case 'hvac':
            return (
              <mesh key={i} position={[item.x, baseY + 0.15 * s, item.z]} rotation={[0, item.rotY, 0]} castShadow>
                <boxGeometry args={[0.5 * s, 0.3 * s, 0.4 * s]} />
                <meshStandardMaterial color="#3a4045" roughness={0.6} metalness={0.4} />
              </mesh>
            );
          case 'vent':
            return (
              <mesh key={i} position={[item.x, baseY + 0.08 * s, item.z]} rotation={[0, item.rotY, 0]} castShadow>
                <cylinderGeometry args={[0.12 * s, 0.12 * s, 0.16 * s, 8]} />
                <meshStandardMaterial color="#4a5058" roughness={0.5} metalness={0.45} />
              </mesh>
            );
          case 'antenna':
            return (
              <mesh key={i} position={[item.x, baseY + 0.35 * s, item.z]} rotation={[0, item.rotY, 0]} castShadow>
                <cylinderGeometry args={[0.02 * s, 0.02 * s, 0.7 * s, 6]} />
                <meshStandardMaterial color="#555a60" roughness={0.4} metalness={0.6} />
              </mesh>
            );
          case 'pipe':
            return (
              <mesh key={i} position={[item.x, baseY + 0.05 * s, item.z]} rotation={[0, item.rotY, Math.PI / 2]} castShadow>
                <boxGeometry args={[0.6 * s, 0.08 * s, 0.08 * s]} />
                <meshStandardMaterial color="#3d4348" roughness={0.55} metalness={0.5} />
              </mesh>
            );
          default:
            return null;
        }
      })}

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[ringRadius, 0.05, 8, 48]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={1} roughness={0.3} />
        </mesh>
      )}
    </group>
  );
}
